import deepEqual from "lodash.isequal";
import bus from "../../bus";
import { playerEvents, playableItemEvents } from "../events";
import Playable from "./playable";
import PlayableItemNull from "./items/null";
import { queueEvents } from "../events";

async function importClip() {
  return (await import("./items/clip")).default
}

async function importFragment() {
  return (await import("./items/fragment")).default;
}

async function importTrack() {
  return (await import("./items/track")).default;
}

/**
 * Resolve a playable class based on `type`
 * @param {String} type Type string for playables
 */
async function getPlayableForType(type) {
  const playableImportMappings = {
    podcast_episode: importClip,
    online_episode: importClip,
    audio_archive_item: importClip,
    broadcast_episode: importFragment,
    clip: importClip,
    fragment: importFragment,
    track: importTrack
  };

  const match = playableImportMappings[type];
  if (match) {
    return await match();
  }

  return PlayableItemNull;
}

/**
 * PlayableQueue
 *
 * Needs to implement the Playable API
 */
class PlayableQueue extends Playable {
  constructor(props) {
    super(props);
    this.onItemStatusChange = this.onItemStatusChange.bind(this);
    this.item = new PlayableItemNull();
    this._prevItem = null;
    this._playables = {};
    this.prevProps = null;
    this.set(props);
  }

  getPlayable(props) {
    return new Promise((resolve, reject) => {
      // FIXME: This is a mess, but it properly memoizes the playable call
      const { item } = props;
      if (
        !this.prevProps ||
        !deepEqual(item, this.prevProps.state.queue.currentItem)
      ) {
        getPlayableForType(props.item.type).then(Playable => {
          if (this.prevProps && this.prevProps.state.queue.currentItem) {
            this._playables[this.prevProps.state.queue.currentItem.id] = null;
          }
          this._playables[item.id] = new Playable(props);
          resolve(this._playables[item.id]);
          bus.emit(queueEvents.RESOLVE);
        });
      } else {
        if (this._playables[item.id]) {
          resolve(this._playables[item.id]);
        } else {
          reject();
        }
      }
    });
  }

  set(nextProps) {
    const { state } = nextProps;
    this.prevProps = this.props;
    this.props = nextProps;
    this.onUpdate();
    // Store a references to the `currentItem` for the _next_ update
    this._prevItem = state.queue.currentItem;
  }

  onUpdate() {
    const { state } = this.props;
    const item = state.queue.currentItem;
    const availableStreams = state.streams.available;
    let { status, volume, muted, quality } = state.player;
    volume = muted ? 0 : volume;

    if (item != null) {
      this.getPlayable({
        item
      })
        .then(playable => {
          this.item = playable;
          // Call set immediately but separately as we want to ignore player status
          // as part of the memoisation call
          this.item.set({
            item,
            status,
            quality,
            volume,
            availableStreams
          });
          // Bind listeners (once)
          this.item.removeListener(
            playableItemEvents.STATUS_CHANGE,
            this.onItemStatusChange
          );
          this.item.on(
            playableItemEvents.STATUS_CHANGE,
            this.onItemStatusChange
          );
        })
        .catch(err => {
          // noop
        });
    } else {
      // Update the playable null
      this.item.set({ status });
    }
  }

  currentItem() {
    return this.item;
  }

  prev() {
    const { actions } = this.props;
    actions.queue.prev();
    return true;
  }

  next() {
    const { actions } = this.props;
    actions.queue.next();
  }

  canPlayNext() {
    const { state } = this.props;
    return state.queue.upcomingItems.length > 0;
  }

  canPlayPrev() {
    const { state } = this.props;
    return state.queue.playedItems.length > 0;
  }

  play() {
    this.currentItem().play();
  }

  pause() {
    this.currentItem().pause();
  }

  stop() {
    this.currentItem().stop();
  }

  canToggleQuality() {
    return this.currentItem().canToggleQuality();
  }

  onItemStatusChange({ status }) {
    const { actions } = this.props;
    switch (status) {
      case "playing":
        bus.emit(playerEvents.PLAY);
        break;
      case "paused":
        bus.emit(playerEvents.PAUSE);
        break;
      case "stopped":
        bus.emit(playerEvents.STOP);
        break;
      case "ended":
        if (this.canPlayNext()) {
          actions.queue.next();
        } else {
          bus.emit(playerEvents.STOP);
        }
        break;
      default:
        break;
    }
  }
}

export default PlayableQueue;
